import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Clock, Search, Sparkles, Skull, Eye, EyeOff, UserPlus, Stethoscope, Swords, ShieldCheck, ShieldOff, Megaphone, Lock, Check, Crown, Gavel, Map, ArrowUpDown, Shield, Scroll, Zap } from "lucide-react";

// Custom Helmet Icon for Roster - fills container
const HelmetIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/helmet-icon.png" 
    alt="Roster" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import api, { apiErr } from "@/lib/api";
import GoldenCrownIcon from "@/components/GoldenCrownIcon";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import GladiatorCard, { LifecycleBadge } from "@/components/GladiatorCard";
import { StatBars } from "@/components/StatBars";
import { AboutBox } from "@/components/AboutBox";
import Modal from "@/components/Modal";
import BeastIcon from "@/components/BeastIcon";
import { CLASS_META } from "@/lib/game";
import { useAuth } from "@/context/AuthContext";
import TraitSelectionModal from "@/components/TraitSelectionModal";
import { TraitDisplay } from "@/components/TraitDisplay";
import TraitAssignmentBanner from "@/components/TraitAssignmentBanner";

// Vivarium Link Component
function VivariumLink() {
  const navigate = useNavigate();
  const [vivariumData, setVivariumData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vivarium")
      .then((r) => setVivariumData(r.data))
      .catch(() => setVivariumData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const vivarium = vivariumData?.vivarium;
  const beastCount = vivariumData?.beast_count || 0;
  const capacity = vivarium?.capacity || 3;

  return (
    <button
      onClick={() => navigate("/vivarium")}
      data-testid="vivarium-link"
      className="w-full flex items-center justify-between gap-4 p-4 border border-teal-800/40 bg-teal-950/10 hover:bg-teal-950/20 hover:border-teal-700/60 rounded-sm transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <BeastIcon size={24} className="text-teal-400" />
        <div>
          <div className="text-sm font-cinzel text-teal-300 group-hover:text-teal-200 transition-colors">
            {vivarium?.built ? "Vivarium" : "Build a Vivarium"}
          </div>
          <div className="text-xs text-zinc-500">
            {vivarium?.built 
              ? `${beastCount}/${capacity} beasts · Capture wild creatures to empower your gladiators`
              : "Construct a beast enclosure to capture exotic creatures"
            }
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-zinc-500 group-hover:text-zinc-400 transition-colors">
        {vivarium?.built ? (
          <span className="text-xs">{beastCount}/{capacity}</span>
        ) : null}
        <BeastIcon size={18} />
      </div>
    </button>
  );
}

// Sanctum Link Component
function SanctumLink() {
  const navigate = useNavigate();
  const [sanctumData, setSanctumData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sanctum")
      .then((r) => setSanctumData(r.data))
      .catch(() => setSanctumData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const doctoreCount = sanctumData?.doctores?.length || 0;
  const maxSlots = sanctumData?.sanctum_slots || 1;

  return (
    <button
      onClick={() => navigate('/sanctum')}
      data-testid="sanctum-link"
      className="w-full flex items-center justify-between gap-4 p-4 border border-amber-800/40 bg-amber-950/10 hover:bg-amber-950/20 hover:border-amber-700/60 rounded-sm transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <Shield size={24} className="text-amber-400" />
        <div>
          <div className="text-sm font-cinzel text-amber-300 group-hover:text-amber-200 transition-colors">
            Doctore's Sanctum
          </div>
          <div className="text-xs text-zinc-500">
            {doctoreCount > 0 
              ? `${doctoreCount}/${maxSlots} master trainers · Specialize your gladiators' training`
              : "Convert retired gladiators into master trainers"
            }
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-zinc-500 group-hover:text-zinc-400 transition-colors">
        <span className="text-xs">{doctoreCount}/{maxSlots}</span>
        <Shield size={18} />
      </div>
    </button>
  );
}


// Skill Tree Manager Link Component
function SkillTreeManagerLink() {
  const navigate = useNavigate();
  const [readyCount, setReadyCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUnlockableGlads = async () => {
      try {
        const { data } = await api.get("/gladiators");
        const withPoints = (data.active || []).filter(g => g.skill_points > 0);
        
        let countWithUnlockable = 0;
        
        for (const glad of withPoints) {
          try {
            const { data: treeData } = await api.get(`/gladiators/${glad.id}/skill-tree`);
            const hasUnlockableSkills = treeData.branches.some(branch => 
              branch.nodes.some(node => node.can_unlock === true)
            );
            if (hasUnlockableSkills) countWithUnlockable++;
          } catch (e) {
            // Skip if can't load skill tree
          }
        }
        
        setReadyCount(countWithUnlockable);
      } catch (err) {
        setReadyCount(0);
      } finally {
        setLoading(false);
      }
    };
    
    checkUnlockableGlads();
  }, []);

  if (loading) return null;
  if (readyCount === 0) return null; // Hide if no gladiators have unlockable skills

  return (
    <button
      onClick={() => navigate("/skill-tree-manager")}
      data-testid="skill-tree-manager-link"
      className="w-full flex items-center justify-between gap-4 p-4 border border-orange-800/40 bg-orange-950/10 hover:bg-orange-950/20 hover:border-orange-700/60 rounded-sm transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <div>
          <div className="text-sm font-cinzel text-orange-300 group-hover:text-orange-200 transition-colors">
            Skill Tree Manager
          </div>
          <div className="text-xs text-zinc-500">
            Upgrade techniques for all gladiators in one place
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 border border-orange-700/50 bg-orange-950/30 rounded-sm">
        <Zap size={14} className="text-orange-400" />
        <span className="font-cinzel text-orange-300 tabular-nums">{readyCount}</span>
        <span className="text-[9px] uppercase tracking-widest text-zinc-600">ready</span>
      </div>
    </button>
  );
}


export default function Roster() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [active, setActive] = useState(null);
  const [sel, setSel] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [scoutOpen, setScoutOpen] = useState(false);
  const [scoutNoConfirm, setScoutNoConfirm] = useState(() => localStorage.getItem("scout_no_confirm") === "1");
  const [medicusScar, setMedicusScar] = useState(null);
  const [cryOpen, setCryOpen] = useState(false);
  const [finisherOpen, setFinisherOpen] = useState(false);
  const [finisherName, setFinisherName] = useState("");
  const [finisherDescription, setFinisherDescription] = useState("");
  const [finisherEligibilityMessage, setFinisherEligibilityMessage] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [parAequa, setParAequa] = useState(null);
  const [retireStatus, setRetireStatus] = useState(null);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("roster_sort") || "age");
  const [needsTraits, setNeedsTraits] = useState([]);
  const [traitModalGlad, setTraitModalGlad] = useState(null);
  const [instantScoutOpen, setInstantScoutOpen] = useState(false);
  const [instantScoutGlad, setInstantScoutGlad] = useState(null);
  const [instantScoutCount, setInstantScoutCount] = useState(0);
  const [showInstantScoutBanner, setShowInstantScoutBanner] = useState(false);
  const [medicusOpen, setMedicusOpen] = useState(false);
  const [medicusGlad, setMedicusGlad] = useState(null);
  const [medicusCount, setMedicusCount] = useState(0);
  const [showMedicusBanner, setShowMedicusBanner] = useState(false);
  const [medicusConfirm, setMedicusConfirm] = useState(null); // {gladiator, scar, scarIndex}
  const [recoveryEtherOpen, setRecoveryEtherOpen] = useState(false);
  const [recoveryEtherGlad, setRecoveryEtherGlad] = useState(null);
  const [recoveryEtherCount, setRecoveryEtherCount] = useState(0);
  const [showRecoveryEtherBanner, setShowRecoveryEtherBanner] = useState(false);

  const load = () => api.get("/gladiators").then((r) => { setActive(r.data.active); setParAequa(r.data.par_aequa || null); setRetireStatus(r.data.retire_status || null); }).catch(() => setActive([]));
  
  const loadInstantScoutCount = async () => {
    try {
      const { data } = await api.get("/inventory");
      const scoutItem = (data.consumables || []).find(c => c.id === "instant_scout");
      const medicusItem = (data.consumables || []).find(c => c.id === "experienced_medicus");
      const etherItem = (data.consumables || []).find(c => c.id === "recovery_ether");
      setInstantScoutCount(scoutItem?.count || 0);
      setMedicusCount(medicusItem?.count || 0);
      setRecoveryEtherCount(etherItem?.count || 0);
    } catch (e) {
      setInstantScoutCount(0);
      setMedicusCount(0);
      setRecoveryEtherCount(0);
    }
  };

  const useInstantScout = async () => {
    if (!instantScoutGlad || instantScoutCount < 1) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${instantScoutGlad.id}/instant-scout`);
      const potentialGrade = result.gladiator.potential_grade || result.gladiator.potential_band?.label || 'Unknown';
      toast.success(`${result.gladiator.name} fully scouted! Potential: ${potentialGrade}`);
      // Update the gladiator in the list
      setActive(active.map(g => g.id === result.gladiator.id ? result.gladiator : g));
      setInstantScoutCount(result.remaining_items);
      setInstantScoutOpen(false);
      setInstantScoutGlad(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const applyMedicus = async () => {
    if (!medicusConfirm || medicusCount < 1) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${medicusConfirm.gladiator.id}/use-medicus`, {
        gladiator_id: medicusConfirm.gladiator.id,
        scar_index: medicusConfirm.scarIndex
      });
      toast.success(result.message, { icon: "⚕️" });
      // Update the gladiator in the list with the new scars array
      setActive(active.map(g => g.id === medicusConfirm.gladiator.id ? { ...g, scars: result.scars } : g));
      setMedicusCount(result.medicus_remaining);
      setMedicusConfirm(null);
      setMedicusOpen(false);
      setMedicusGlad(null);
      await load(); // Reload to get full updated data
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const applyRecoveryEther = async () => {
    if (!recoveryEtherGlad) return;
    
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${recoveryEtherGlad.id}/use-recovery-ether`);
      toast.success(result.message, { icon: "⚡" });
      setRecoveryEtherCount(result.ether_remaining);
      setRecoveryEtherOpen(false);
      setRecoveryEtherGlad(null);
      await load(); // Reload to get full updated data
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  useEffect(() => {
    load();
    loadInstantScoutCount();
    // Fetch gladiators needing traits
    api.get("/gladiators/needs-traits").then((r) => {
      setNeedsTraits(r.data.gladiators || []);
    }).catch((e) => {
      console.error('[TRAITS ROSTER] API error:', e);
      setNeedsTraits([]);
    });
    
    // Check if user came from Dashboard to use Instant Scout or Medicus or Recovery Ether
    const params = new URLSearchParams(window.location.search);
    if (params.get('useItem') === 'instant_scout') {
      setShowInstantScoutBanner(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('useItem') === 'experienced_medicus') {
      setShowMedicusBanner(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('useItem') === 'recovery_ether') {
      setShowRecoveryEtherBanner(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Auto-refresh every 30 seconds to show stat updates from quests
    const interval = setInterval(() => {
      load();
      loadInstantScoutCount();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const changeSortBy = (sort) => {
    setSortBy(sort);
    localStorage.setItem("roster_sort", sort);
  };

  const openCry = () => {
    setCryOpen(true);
    if (!catalog) api.get("/battle-cries").then((r) => setCatalog(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  const openFinisher = async () => { try { setFinisherEligibilityMessage(""); const { data } = await api.get(`/gladiators/${sel.id}/finisher`); setFinisherName(data.finisher?.name || ""); setFinisherDescription(data.finisher?.description || ""); setFinisherOpen(true); } catch (e) { if (e?.response?.status === 403) { setFinisherEligibilityMessage("Your gladiator cannot create a finisher move until they have reached the Gold division."); setFinisherOpen(true); } else toast.error(apiErr(e)); } };
  const saveFinisher = async () => { try { const { data } = await api.put(`/gladiators/${sel.id}/finisher`, { name: finisherName, description: finisherDescription }); setSel({ ...sel, custom_finisher: data.finisher }); setFinisherOpen(false); toast.success("Finisher saved"); } catch (e) { toast.error(apiErr(e)); } };
  const deleteFinisher = async () => { try { await api.delete(`/gladiators/${sel.id}/finisher`); setSel({ ...sel, custom_finisher: null }); setFinisherOpen(false); toast.success("Finisher removed"); } catch (e) { toast.error(apiErr(e)); } };

  const chooseCry = async (cryId) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/battle-cry`, { cry_id: cryId });
      setSel((s) => ({ ...s, battle_cry: data.battle_cry, battle_cry_text: data.battle_cry_text }));
      setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, battle_cry: data.battle_cry, battle_cry_text: data.battle_cry_text } : x));
      toast.success(cryId ? `Battle cry set: \u201c${data.battle_cry_text}\u201d` : "Battle cry cleared");
      setCryOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openGlad = (g) => { setSel(g); setRenameVal(""); };

  const chooseCampaignTitle = async (title) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/campaign-title`, { title });
      setSel((s) => ({ ...s, campaign_title: data.campaign_title }));
      setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, campaign_title: data.campaign_title } : x));
      toast.success(title ? `Campaign title set: ${title}` : "Campaign title hidden");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doRename = async () => {
    if (!renameVal.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/rename`, { name: renameVal.trim() });
      toast.success("Gladiator renamed");
      setActive((prev) => prev.map((x) => x.id === data.id ? data : x));
      setSel(data); setRenameVal("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doDoctore = async (scar) => {
    setMedicusScar(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/doctore`, { scar_id: scar.id });
      (data.outcome === "healed" ? toast.success : toast.error)(data.message);
      setSel(data.gladiator);
      setActive((prev) => prev.map((x) => x.id === data.gladiator.id ? data.gladiator : x));
      refreshUser?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doScout = async () => {
    setScoutOpen(false);
    if (scoutNoConfirm) localStorage.setItem("scout_no_confirm", "1");
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/scout`);
      setSel(data.gladiator);
      setActive((prev) => prev.map((x) => x.id === data.gladiator.id ? data.gladiator : x));
      toast.success(data.gladiator.potential_band?.exact
        ? `Scouting complete — ${data.gladiator.name}'s potential is ${data.gladiator.potential_grade}!`
        : `Scouts report back — potential now ${data.gladiator.potential_grade}`);
      refreshUser?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

  const setAutoTactic = async (tactic) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/auto-tactic`, { tactic });
      setSel((s) => ({ ...s, auto_tactic: data.auto_tactic }));
      setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, auto_tactic: data.auto_tactic } : x));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleProtect = async () => {
    const np = !sel.protected_from_challenges;
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/protect`, { protect: np });
      setSel((s) => ({ ...s, protected_from_challenges: data.protected_from_challenges }));
      setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, protected_from_challenges: data.protected_from_challenges } : x));
      toast.success(data.protected_from_challenges ? `${sel.name} is now protected from auto-accepted challenges` : `${sel.name} will auto-accept challenges again`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleSkills = async () => {
    const ns = sel.show_skills === false; // flipping to show=true if currently hidden
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/skills-visibility`, { show: ns });
      setSel((s) => ({ ...s, show_skills: data.show_skills }));
      setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, show_skills: data.show_skills } : x));
      toast.success(data.show_skills ? "Skills are now public" : "Skills hidden from the public");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };


  const handleAssignTraits = async (traits) => {
    if (!traitModalGlad) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${traitModalGlad.id}/assign-traits`, traits);
      setActive((prev) => prev.map((g) => g.id === data.id ? data : g));
      if (sel?.id === data.id) setSel(data);
      setNeedsTraits((prev) => prev.filter((g) => g.id !== traitModalGlad.id));
      setTraitModalGlad(null);
      toast.success(`Traits assigned to ${data.name}!`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openTraitAssignment = () => {
    if (needsTraits.length === 0) return;
    const glad = active.find(g => needsTraits.some(n => n.id === g.id));
    if (glad) setTraitModalGlad(glad);
  };

  const freeGrace = !!retireStatus?.free_grace;
  const needsPay = retireStatus ? (!freeGrace && retireStatus.remaining <= 0) : false;
  const retireCost = retireStatus?.cost ?? 100;
  const cannotAfford = needsPay && (retireStatus?.gold ?? 0) < retireCost;

  const doForceRetire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${sel.id}/retire`, { pay: needsPay });
      toast.success(data.charged ? `${sel.name} retired — ${data.charged} gold paid` : `${sel.name} has been retired with honor`);
      setActive((prev) => prev.filter((x) => x.id !== sel.id));
      if (data.retire_status) setRetireStatus((s) => ({ ...(s || {}), ...data.retire_status, gold: (s?.gold ?? 0) - (data.charged || 0) }));
      if (data.charged) refreshUser?.();
      setRetireOpen(false);
      setSel(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (active === null) return <Loader />;

  const rosterCap = user?.roster_cap || active.length;
  const canBuyMore = ((user?.extra_slots ?? 0) + (user?.admin_bonus_slots ?? 0)) < (user?.max_extra_slots ?? 5);
  const atCap = active.length >= rosterCap;

  return (
    <div>
      <PageHeader title="Active Roster" icon={HelmetIcon}
        subtitle={
          <span data-testid="roster-cap-subtitle" className="flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0">
            <span>
              <span className={atCap ? "text-amber-300 font-semibold" : ""}>{active.length} / {rosterCap}</span> fighters under your banner
            </span>
            {canBuyMore && (
              <>
                <span className="hidden sm:inline">&nbsp;·&nbsp;</span>
                <button data-testid="expand-ludus-link" onClick={() => navigate("/vip")}
                  className="text-sky-400 hover:text-sky-300 underline underline-offset-2 transition-colors">
                  {atCap ? "Roster full — add slots" : "Expand your ludus"}
                </button>
              </>
            )}
          </span>
        }
        action={
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 w-full lg:w-auto">
            <button data-testid="roster-challenge-btn" onClick={() => navigate("/challenges")}
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 font-cinzel uppercase tracking-[0.12em] sm:tracking-[0.15em] text-sm rounded-sm border border-amber-600/60 text-amber-300 hover:bg-amber-900/30 hover:border-amber-500 transition-colors w-full lg:w-auto">
              <Swords size={16} /> Send Challenge
            </button>
            <button data-testid="roster-recruit-btn" onClick={() => navigate("/recruit")}
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 font-cinzel uppercase tracking-[0.12em] sm:tracking-[0.15em] text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors recruit-pulse w-full lg:w-auto">
              <UserPlus size={16} /> Recruit Pool
            </button>
          </div>
        } />
      
      {/* Trait Assignment Banner */}
      <TraitAssignmentBanner count={needsTraits.length} onClick={openTraitAssignment} />

      {parAequa && (
        <div data-testid="par-aequa-entry-banner" className="mb-5 flex items-center gap-3 border border-amber-700/40 bg-gradient-to-r from-amber-950/30 to-zinc-950 px-4 py-3 rounded-sm">
          <Crown size={18} className="text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-200 font-cinzel truncate">
              Par Aequa entry: <span className="text-amber-100">{parAequa.name}</span>
              <span className="text-zinc-500 font-manrope"> · {parAequa.cls} · {parAequa.total_skill} skill</span>
            </p>
            <p className="text-[11px] text-zinc-500">Temporary tournament fighter — does not count toward your roster cap.</p>
          </div>
          <button data-testid="par-aequa-goto-btn" onClick={() => navigate("/events?tab=tournaments")}
            className="shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-amber-700/50 text-amber-300 hover:bg-amber-900/30 hover:border-amber-500 rounded-sm transition-colors">
            View
          </button>
        </div>
      )}
      
      {/* Vivarium & Sanctum Links - placed above roster */}
      <div className="mb-5 space-y-3">
        <VivariumLink />
        <SanctumLink />
        <SkillTreeManagerLink />
      </div>
      
      {/* Ready for Battle Section */}
      {active && active.length > 0 && (() => {
        const readyGladiators = active.filter(g => {
          // Must be fight_ready (not resting)
          if (!g.fight_ready) return false;
          
          // Not on timed quest
          if (g.on_timed_quest) return false;
          
          return true;
        });
        
        return readyGladiators.length > 0 ? (
          <div className="mb-5 border border-orange-800/40 bg-orange-950/10 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-cinzel text-orange-300 font-semibold">
                Gladiators ready for battle:
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {readyGladiators.map(g => (
                <button
                  key={g.id}
                  onClick={() => openGlad(g)}
                  className="px-3 py-1.5 text-sm border border-orange-700/50 bg-orange-900/20 hover:bg-orange-900/40 hover:border-orange-600 text-orange-200 rounded-sm transition-colors"
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        ) : null;
      })()}
      
      {/* Instant Scout Guidance Banner */}
      {showInstantScoutBanner && instantScoutCount > 0 && (
        <div className="mb-6 bg-orange-900/20 border border-orange-600/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Scroll className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-cinzel text-orange-400 font-semibold flex items-center gap-2">
                How to Use Instant Scout
                <button
                  onClick={() => setShowInstantScoutBanner(false)}
                  className="ml-auto text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <Check size={16} />
                </button>
              </h3>
              <p className="text-sm text-zinc-300 mt-1">
                Scroll down and look for gladiators that aren't fully scouted. Click the orange <span className="font-semibold text-orange-400">"Use Instant Scout"</span> button below their card to instantly reveal their true potential.
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                You have <span className="text-orange-400 font-semibold">{instantScoutCount}</span> Instant Scout scroll{instantScoutCount !== 1 ? 's' : ''} available.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Experienced Medicus Guidance Banner */}
      {showMedicusBanner && medicusCount > 0 && (
        <div className="mb-6 bg-orange-900/20 border border-orange-600/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Stethoscope className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-cinzel text-orange-400 font-semibold flex items-center gap-2">
                How to Use Experienced Medicus
                <button
                  onClick={() => setShowMedicusBanner(false)}
                  className="ml-auto text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <Check size={16} />
                </button>
              </h3>
              <p className="text-sm text-zinc-300 mt-1">
                Scroll down and look for gladiators with scars. Click the orange <span className="font-semibold text-orange-400">&quot;Use Experienced Medicus&quot;</span> button below their card to heal one scar with 100% success rate.
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                You have <span className="text-orange-400 font-semibold">{medicusCount}</span> Experienced Medicus item{medicusCount !== 1 ? 's' : ''} available.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Ether Guidance Banner */}
      {showRecoveryEtherBanner && recoveryEtherCount > 0 && (
        <div className="mb-6 bg-orange-900/20 border border-orange-600/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-cinzel text-orange-400 font-semibold flex items-center gap-2">
                How to Use Recovery Ether
                <button
                  onClick={() => setShowRecoveryEtherBanner(false)}
                  className="ml-auto text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <Check size={16} />
                </button>
              </h3>
              <p className="text-sm text-zinc-300 mt-1">
                Scroll down and look for gladiators with active rest cooldowns. Click the orange <span className="font-semibold text-orange-400">&quot;Use Recovery Ether&quot;</span> button below their card to reduce rest time by 2 hours.
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                You have <span className="text-orange-400 font-semibold">{recoveryEtherCount}</span> Recovery Ether item{recoveryEtherCount !== 1 ? 's' : ''} available.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {active.length === 0 ? (
        <Empty label="You need gladiators, Lanista! Head to the recruit pool now!">
          <button className={btn()} onClick={() => navigate("/recruit")}>Recruit Gladiators</button>
        </Empty>
      ) : (
        <>
          {/* Sort Controls */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
              <ArrowUpDown size={12} /> Sort by:
            </span>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "age", label: "Age" },
                { id: "stats", label: "Total Stats" },
                { id: "rank", label: "Rank" },
                { id: "potential", label: "Potential" }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  data-testid={`sort-${id}-btn`}
                  onClick={() => changeSortBy(id)}
                  className={`px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide rounded-sm transition-colors ${
                    sortBy === id
                      ? "bg-amber-500 text-zinc-950"
                      : "border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const sorted = [...active];
              if (sortBy === "stats") {
                sorted.sort((a, b) => (b.total_skill || 0) - (a.total_skill || 0));
              } else if (sortBy === "rank") {
                sorted.sort((a, b) => (a.rank || 999999) - (b.rank || 999999));
              } else if (sortBy === "potential") {
                // Priority 1: Avoid rapid_decline
                // Priority 2: Fully scouted
                // Priority 3: Potential grade (Elite > Excellent > Good > Average > Poor)
                const gradeValue = (grade) => {
                  if (!grade) return 0;
                  if (grade === "Elite") return 5;
                  if (grade === "Excellent") return 4;
                  if (grade === "Good") return 3;
                  if (grade === "Average") return 2;
                  if (grade === "Poor") return 1;
                  return 0;
                };
                sorted.sort((a, b) => {
                  // Rapid decline goes to bottom
                  const aRapid = a.lifecycle === "rapid_decline";
                  const bRapid = b.lifecycle === "rapid_decline";
                  if (aRapid && !bRapid) return 1;
                  if (!aRapid && bRapid) return -1;
                  
                  // Then by fully scouted
                  const aFullyScouted = (a.potential_band?.exact === true);
                  const bFullyScouted = (b.potential_band?.exact === true);
                  if (aFullyScouted && !bFullyScouted) return -1;
                  if (!aFullyScouted && bFullyScouted) return 1;
                  
                  // Then by potential grade
                  return gradeValue(b.potential_grade) - gradeValue(a.potential_grade);
                });
              } else {
                // age - keep default backend sort (oldest first)
                sorted.sort((a, b) => (b.age || 0) - (a.age || 0));
              }
              return sorted.map((g) => (
                <div key={g.id}>
                  <GladiatorCard g={g} onClick={() => openGlad(g)} enableBaseStatsToggle />
                  <TraitDisplay gladiator={g} compact={true} />
                  {/* Instant Scout Button */}
                  {!g.retired && !g.potential_band?.exact && instantScoutCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setInstantScoutGlad(g);
                        setInstantScoutOpen(true);
                      }}
                      className="w-full mt-2 px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide bg-orange-600 text-white hover:bg-orange-500 transition-colors rounded-sm flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Scroll size={14} /> Use Instant Scout
                    </button>
                  )}
                  {/* Experienced Medicus Button */}
                  {!g.retired && g.scars && g.scars.length > 0 && medicusCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setMedicusGlad(g);
                        setMedicusOpen(true);
                      }}
                      className="w-full mt-2 px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide bg-orange-600 text-white hover:bg-orange-500 transition-colors rounded-sm flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Stethoscope size={14} /> Use Experienced Medicus ({g.scars.length} scar{g.scars.length !== 1 ? 's' : ''})
                    </button>
                  )}
                  {/* Recovery Ether Button */}
                  {!g.retired && recoveryEtherCount > 0 && g.fight_cooldown_seconds > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setRecoveryEtherGlad(g);
                        setRecoveryEtherOpen(true);
                      }}
                      className="w-full mt-2 px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide bg-orange-600 text-white hover:bg-orange-500 transition-colors rounded-sm flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Zap size={14} /> Use Recovery Ether (Reduce rest by 2hrs)
                    </button>
                  )}
                </div>
              ));
            })()}
          </div>
        </>
      )}

      <Modal open={!!sel} onClose={() => setSel(null)} title={
        sel ? (
          <button data-testid="roster-glad-owner-link" onClick={() => { const id = sel.id; setSel(null); navigate(`/gladiator/${id}`); }} className="hover:text-amber-100 transition-colors underline decoration-amber-700/40 underline-offset-4">
            {sel.name}
          </button>
        ) : ""
      } wide>
        {sel && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {(() => { const I = CLASS_META[sel.cls]?.icon; return I ? <I className={CLASS_META[sel.cls].color} size={20} /> : null; })()}
                <span className="font-cinzel text-amber-300 uppercase tracking-wide">{sel.cls}</span>
                <LifecycleBadge lifecycle={sel.lifecycle} />
              </div>
              <p className="text-sm text-zinc-400 mb-4">{sel.class_info?.desc}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Age" value={sel.age} />
                <Info label="Peak Overall" value={sel.peak_overall} />
                <Info label="Current Overall" value={sel.overall} />
                <Info label="Rating" value={sel.rating} />
                <Info label="Record" value={`${sel.wins}W / ${sel.losses}L`} />
                <Info label="Potential" value={sel.potential_grade} />
                <Info label="Weapon" value={sel.class_info?.weapon} />
                <Info label="Offhand" value={sel.class_info?.offhand || "—"} />
              </div>

              {sel.potential_band && !sel.potential_band.exact && (
                <div className="mt-3 border border-violet-900/40 bg-violet-950/20 px-3 py-2">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-violet-300/80">
                    <span className="flex items-center gap-1.5"><Search size={12} /> Scouting</span>
                    <span>{Math.round((sel.potential_band.progress || 0) * 100)}%</span>
                  </div>
                  <div className="w-full h-1 bg-zinc-800 mt-1.5 overflow-hidden">
                    <div className="h-full bg-violet-500 transition-all" style={{ width: `${Math.round((sel.potential_band.progress || 0) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    {sel.potential_band.revealed
                      ? <>Estimated potential <span className="text-violet-300">{sel.potential_band.label}</span> — keep training to sharpen the read.</>
                      : <>Not yet scouted. Train this fighter to begin reading their potential.</>}
                  </p>
                  <button data-testid="scout-gladiator-btn" onClick={() => scoutNoConfirm ? doScout() : setScoutOpen(true)} disabled={busy}
                    title="Send scouts to narrow this gladiator's potential range"
                    className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1.5 border border-violet-700/50 text-violet-300 hover:bg-violet-900/30 hover:border-violet-500 rounded-sm transition-colors disabled:opacity-50">
                    <Search size={12} /> Scout (+25%) — 100g
                  </button>
                </div>
              )}

              <div className="mt-4">
                {sel.renamed && !sel.free_rename_available ? (
                  <p className="text-[11px] text-zinc-600 flex items-center gap-1.5"><Pencil size={12} /> This gladiator has used their one rename.</p>
                ) : (
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                      <Pencil size={12} /> 
                      {sel.free_rename_available ? (
                        <span className="text-amber-400">Free Rename Available (New Owner)</span>
                      ) : (
                        "Rename (one time only)"
                      )}
                    </label>
                    {sel.free_rename_available && (
                      <p className="text-[10px] text-zinc-500 mt-0.5">You won this gladiator from auction - you get one free rename!</p>
                    )}
                    <div className="flex gap-2 mt-1.5">
                      <input data-testid="rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} maxLength={40}
                        placeholder="New name" className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
                      <button data-testid="rename-btn" disabled={busy || !renameVal.trim()} onClick={doRename} className={btn() + " py-2 px-4"}>Save</button>
                    </div>
                  </div>
                )}
              </div>

              {sel.scars && sel.scars.length > 0 && (
                <div className="mt-4" data-testid="glad-scars">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Skull size={12} /> Battle Scars ({sel.scars.length}/3)</label>
                  <div className="mt-1.5 space-y-2">
                    {sel.scars.map((sc) => (
                      <div key={sc.id} data-testid={`scar-${sc.id}`} className="border border-red-900/40 bg-red-950/15 px-3 py-2 rounded-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-red-200 font-cinzel">{sc.name}</span>
                          <span className="text-[11px] tabular-nums whitespace-nowrap">
                            <span className="text-red-400">−{sc.pct}% {cap(sc.neg)}</span>{" "}
                            <span className="text-emerald-400">+{sc.pct}% {cap(sc.pos)}</span>
                          </span>
                        </div>
                        <div className="text-[10px] text-orange-400 mt-1">−2% training speed</div>
                        <button data-testid={`doctore-${sc.id}`} disabled={busy} onClick={() => setMedicusScar(sc)}
                          className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-300 hover:text-amber-200 border border-amber-800/50 hover:border-amber-500 px-2.5 py-1 transition-colors disabled:opacity-50">
                          <Stethoscope size={12} /> Visit the Medicus — 200g
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <AboutBox about={sel.about} canEdit endpoint={`/gladiators/${sel.id}/about`}
                  onSaved={(about) => { setSel((s) => ({ ...s, about })); setActive((prev) => prev.map((x) => x.id === sel.id ? { ...x, about } : x)); }}
                  placeholder="Describe this gladiator's story, fighting style, or legend…" testid="roster-glad-about" />
              </div>

              {sel.auctioned && (
                <p data-testid="auctioned-settings-lock" className="mt-1 mb-1 text-[11px] text-fuchsia-300/90 flex items-center gap-1.5 border border-fuchsia-800/40 bg-fuchsia-950/20 px-2.5 py-1.5 rounded-sm">
                  <Gavel size={12} /> Listed at the Auction House — skills & loadout stay public and auto-accept is locked until the listing ends.
                </p>
              )}
              {(sel.golden_crown || sel.bronze_crown || sel.silver_crown) && (
                <p data-testid="crown-holder-lock" className="mt-1 mb-1 text-[11px] text-amber-300/90 flex items-center gap-1.5 border border-amber-800/40 bg-amber-950/20 px-2.5 py-1.5 rounded-sm">
                  <GoldenCrownIcon size={12} crownName={sel.bronze_crown ? "Bronze Crown" : sel.silver_crown ? "Silver Crown" : "Golden Crown"} /> Crown Holder — auto-accept is mandatory and cannot be disabled while holding the crown.
                </p>
              )}
              <button data-testid="toggle-skills-visibility-btn" onClick={toggleSkills} disabled={busy || sel.auctioned}
                className="mt-1 flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-2 transition-colors w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-zinc-400 disabled:hover:border-zinc-700">
                {sel.show_skills === false ? <><EyeOff size={14} /> Skills hidden — make public</> : <><Eye size={14} /> Skills public — hide from others</>}
              </button>

              <div className="mt-3" data-testid="auto-tactic-box">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Swords size={12} /> Auto-defend tactic</label>
                  <button data-testid="protect-challenges-toggle" onClick={toggleProtect} disabled={busy || sel.auctioned || sel.golden_crown || sel.bronze_crown || sel.silver_crown}
                    title={(sel.golden_crown || sel.bronze_crown || sel.silver_crown) ? "Crown holders must accept all challenges" : "When on, this gladiator won't auto-accept incoming challenges (your other gladiators still will)."}
                    className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      sel.protected_from_challenges ? "border-sky-600 text-sky-300 bg-sky-950/30" : "border-zinc-700 text-zinc-500 hover:border-sky-700/60 hover:text-sky-300"}`}>
                    {sel.protected_from_challenges ? <><ShieldCheck size={12} /> Protected</> : <><ShieldOff size={12} /> Protect</>}
                  </button>
                </div>
                <p data-testid="auto-tactic-default-label" className="mt-3 text-xs text-zinc-500">Default tactic: {cap(user?.auto_accept_tactics || "balanced")}</p>
                <select data-testid="auto-tactic-select" value={sel.auto_tactic || ""} disabled={busy || sel.auctioned}
                  onChange={(e) => setAutoTactic(e.target.value)}
                  className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none rounded-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  <option value="">Use my default tactic</option>
                  <option value="aggressive">Aggressive</option>
                  <option value="defensive">Defensive</option>
                  <option value="balanced">Balanced</option>
                </select>
                {(sel.golden_crown || sel.bronze_crown || sel.silver_crown)
                  ? <p className="text-[11px] text-amber-400/90 mt-1 flex items-center gap-1.5"><GoldenCrownIcon size={12} crownName={sel.bronze_crown ? "Bronze Crown" : sel.silver_crown ? "Silver Crown" : "Golden Crown"} /> As a crown holder, this gladiator must auto-accept all challenges.</p>
                  : sel.protected_from_challenges
                  ? <p className="text-[11px] text-sky-400/80 mt-1">Protected — incoming challenges to this fighter won't be auto-accepted and will wait for your manual response.</p>
                  : <p className="text-[11px] text-zinc-600 mt-1">Tactic this fighter uses when auto-accepting incoming challenges (and revenge fights).</p>}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Current Combat Skills</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-3">Includes gear bonuses.</div>
              <StatBars skills={sel.combat_skills || sel.effective_skills || sel.peak_effective_skills} skillTitles={sel.skill_titles || sel.peak_raw_skill_titles} />
              
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Peak Combat Skills (without gear)</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-3">Titles reflect raw base skills only.</div>
                <StatBars skills={sel.peak_raw_skills} skillTitles={sel.peak_raw_skill_titles || sel.skill_titles} />
              </div>
              
              <div className="mt-4 text-xs flex items-center gap-2">
                <Clock size={13} className={sel.fight_ready ? "text-emerald-400" : "text-orange-400"} />
                {sel.fight_ready
                  ? <span className="text-emerald-400">Ready to fight</span>
                  : <span className="text-orange-400">Resting · ready in ~{Math.ceil((sel.fight_cooldown_seconds || 0) / 3600)}h</span>}
              </div>
              <div className="flex gap-2 mt-5">
                <button className={btn("ghost") + " flex-1"} onClick={() => navigate("/training")}>Train</button>
                <button data-testid="roster-equip-selected-gladiator" className={btn("ghost") + " flex-1"} onClick={() => navigate(`/equipment?tab=armory&gladiator=${encodeURIComponent(sel.id)}`)}>Equip</button>
                <button className={btn() + " flex-1"} onClick={() => navigate("/challenges")}>Challenge</button>
              </div>
              <button data-testid="open-skilltree-btn" className={btn("ghost") + " w-full mt-2 flex items-center justify-center gap-2"} onClick={() => navigate(`/gladiator/${sel.id}/skills`)}>
                <Sparkles size={15} /> Skill Tree
                {sel.skill_points > 0 && (
                  <span data-testid={`skillpoint-badge-${sel.id}`} className="ml-1 px-2 py-0.5 text-[10px] rounded-full bg-amber-500 text-zinc-950 font-bold tabular-nums">{sel.skill_points}</span>
                )}
              </button>
              <button data-testid="open-finisher-editor-btn" className={btn("ghost") + " w-full mt-2 flex items-center justify-center gap-2"} onClick={openFinisher}><Swords size={15} /> {sel.custom_finisher ? "Edit Finisher" : "Create Finisher"}</button>
              <button data-testid="force-retire-btn" disabled={busy} className={btn("danger") + " w-full mt-2 flex items-center justify-center gap-2"} onClick={() => setRetireOpen(true)}>
                <Skull size={15} /> Force Retire
              </button>

              <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="battle-cry-box">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Megaphone size={12} /> Battle Cry</label>
                  <button data-testid="open-battle-cry-btn" onClick={openCry} disabled={busy}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-400 hover:border-amber-600 hover:text-amber-300 rounded-sm transition-colors">
                    {sel.battle_cry ? "Change" : "Choose"}
                  </button>
                </div>
                <p data-testid="current-battle-cry" className={`mt-1.5 text-sm ${sel.battle_cry_text ? "text-amber-200 italic" : "text-zinc-600"}`}>
                  {sel.battle_cry_text ? `\u201c${sel.battle_cry_text}\u201d` : "None — shouted as the fight begins."}
                </p>
              </div>

              {sel.campaign_titles?.length > 0 && (
                <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="campaign-title-box">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Crown size={12} /> Campaign Title</label>
                  <select data-testid="campaign-title-select" value={sel.campaign_title || ""} disabled={busy}
                    onChange={(e) => chooseCampaignTitle(e.target.value)}
                    className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded-sm px-3 py-2 focus:border-amber-500 outline-none">
                    <option value="">— None —</option>
                    {sel.campaign_titles.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <p className="text-[11px] text-zinc-500 mt-1">Earned by conquering provinces — shown on this gladiator's profile &amp; arena nameplate.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      <Modal open={finisherOpen && !!sel} onClose={() => setFinisherOpen(false)} title={sel ? `Custom Finisher — ${sel.name}` : "Custom Finisher"}>
        <div data-testid="finisher-editor" className="space-y-4">{finisherEligibilityMessage ? <p data-testid="finisher-gold-division-notice" className="border border-amber-500 bg-amber-950/40 p-4 text-center font-cinzel text-base text-amber-200">{finisherEligibilityMessage}</p> : <><label className="block text-sm text-zinc-300">Name<input data-testid="finisher-name-input" value={finisherName} onChange={(e) => setFinisherName(e.target.value)} className="mt-1 w-full border border-zinc-700 bg-zinc-950 p-2" /></label><label className="block text-sm text-zinc-300">Description<textarea data-testid="finisher-description-input" value={finisherDescription} onChange={(e) => setFinisherDescription(e.target.value)} className="mt-1 min-h-24 w-full border border-zinc-700 bg-zinc-950 p-2" /></label><div data-testid="finisher-live-preview" className="border border-amber-900/50 p-3 text-sm"><b>FINISHER! {sel?.name} unleashes {finisherName || "Your Finisher"}!</b><p>{finisherDescription || "Your finisher description appears here."}</p></div><div className="flex gap-2"><button data-testid="save-finisher-btn" onClick={saveFinisher} className={btn()}>Save</button>{sel?.custom_finisher && <button data-testid="delete-finisher-btn" onClick={deleteFinisher} className={btn("danger")}>Delete</button>}</div></>}</div>
      </Modal>

      <Modal open={retireOpen && !!sel} onClose={() => setRetireOpen(false)} title="Force Retirement">
        <div data-testid="force-retire-modal" className="space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Force <span className="text-amber-300 font-medium">{sel?.name}</span> into retirement? They will permanently leave your active roster.
          </p>
          <div className="flex items-start gap-2 border border-red-900/50 bg-red-950/20 px-4 py-3 rounded-sm">
            <Skull size={16} className="text-red-400 mt-0.5 shrink-0" />
            <span className="text-sm text-red-200">This <span className="font-semibold">cannot be undone</span> — {sel?.name} will fight no more.</span>
          </div>

          {sel?.on_expedition && (
            <div data-testid="retire-expedition-warning" className="flex items-start gap-2 border border-orange-700/60 bg-orange-950/25 px-4 py-3 rounded-sm">
              <Map size={16} className="text-orange-400 mt-0.5 shrink-0" />
              <span className="text-sm text-orange-200">
                <span className="font-semibold">{sel?.name} is on a campaign expedition.</span> Retiring them will
                abandon the expedition and <span className="font-semibold">all progress in that area will be lost.</span>
              </span>
            </div>
          )}

          {retireStatus && (
            freeGrace ? (
              <div data-testid="retire-grace-notice" className="flex items-start gap-2 border border-emerald-700/50 bg-emerald-950/20 px-4 py-3 rounded-sm">
                <Skull size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-sm text-emerald-200">
                  <span className="font-semibold">Welcome back!</span> As a returning lanista, you can retire your entire current roster <span className="font-semibold">for free</span> — no daily limit — while you rebuild.
                </span>
              </div>
            ) : needsPay ? (
              <div data-testid="retire-paid-notice" className="flex items-start gap-2 border border-amber-700/50 bg-amber-950/20 px-4 py-3 rounded-sm">
                <GoldCoin size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <span className="text-sm text-amber-200">
                  You've used your <span className="font-semibold">{retireStatus.free_per_day} free retirements</span> today. Retiring another costs <span className="font-semibold">{retireCost} gold</span>.
                  {cannotAfford && <span className="block text-red-300 mt-1">Not enough gold ({retireStatus.gold ?? 0}g available).</span>}
                </span>
              </div>
            ) : (
              <p data-testid="retire-free-notice" className="text-[11px] text-zinc-500">
                {retireStatus.remaining} of {retireStatus.free_per_day} free daily retirements remaining.
              </p>
            )
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button data-testid="force-retire-cancel-btn" onClick={() => setRetireOpen(false)} disabled={busy}
              className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Cancel</button>
            <button data-testid="force-retire-confirm-btn" onClick={doForceRetire} disabled={busy || cannotAfford}
              className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-1.5 rounded-sm transition-colors disabled:opacity-50 ${needsPay ? "bg-amber-500 text-zinc-950 hover:bg-amber-400" : "bg-red-600 text-white hover:bg-red-500"}`}>
              {needsPay ? <GoldCoin size={14} /> : <Skull size={14} />}
              {busy ? "Retiring…" : needsPay ? `Retire for ${retireCost} Gold` : "Force Retire"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={scoutOpen && !!sel} onClose={() => setScoutOpen(false)} title="Send Scouts">
        <div data-testid="scout-confirm-modal" className="space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Spend gold to advance <span className="text-violet-300 font-medium">{sel?.name}</span>'s scouting by <span className="text-violet-300 font-medium">+25%</span>, tightening their potential range.
          </p>
          <div className="flex items-start gap-2 border border-amber-700/50 bg-amber-950/20 px-4 py-3 rounded-sm">
            <GoldCoin size={16} className="text-amber-400 mt-0.5 shrink-0" />
            <span className="text-sm text-amber-200">This costs <span className="font-semibold">100 gold</span>. You have <span className="font-semibold">{user?.gold ?? 0}g</span> available.</span>
          </div>
          <label data-testid="scout-dont-ask" className="flex items-center gap-2 text-[12px] text-zinc-400 cursor-pointer select-none">
            <input type="checkbox" checked={scoutNoConfirm} onChange={(e) => setScoutNoConfirm(e.target.checked)}
              className="accent-violet-500 w-4 h-4" />
            Don't show this again
          </label>
          <div className="flex gap-2 justify-end pt-1">
            <button data-testid="scout-cancel-btn" onClick={() => setScoutOpen(false)} disabled={busy}
              className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Cancel</button>
            <button data-testid="scout-confirm-btn" onClick={doScout} disabled={busy || (user?.gold ?? 0) < 100}
              className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-1.5 rounded-sm transition-colors disabled:opacity-50 bg-violet-600 text-white hover:bg-violet-500">
              <Search size={14} /> {busy ? "Scouting…" : "Scout for 100 Gold"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!medicusScar && !!sel} onClose={() => setMedicusScar(null)} title="Visit the Medicus">
        <div data-testid="medicus-confirm-modal" className="space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Send <span className="text-amber-300 font-medium">{sel?.name}</span> to the Medicus to treat <span className="text-red-300 font-medium">"{medicusScar?.name}"</span>?
          </p>
          <div className="flex items-start gap-2 border border-amber-700/50 bg-amber-950/20 px-4 py-3 rounded-sm">
            <GoldCoin size={16} className="text-amber-400 mt-0.5 shrink-0" />
            <span className="text-sm text-amber-200">This costs <span className="font-semibold">200 gold</span>. You have <span className="font-semibold">{user?.gold ?? 0}g</span> available.</span>
          </div>
          <div className="flex items-start gap-2 border border-red-900/50 bg-red-950/20 px-4 py-3 rounded-sm">
            <Skull size={16} className="text-red-400 mt-0.5 shrink-0" />
            <span className="text-sm text-red-200">Treatment is risky — <span className="font-semibold">10%</span> chance it fails, and <span className="font-semibold">10%</span> chance it worsens the scar by 3%.</span>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button data-testid="medicus-cancel-btn" onClick={() => setMedicusScar(null)} disabled={busy}
              className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Cancel</button>
            <button data-testid="medicus-confirm-btn" onClick={() => doDoctore(medicusScar)} disabled={busy || (user?.gold ?? 0) < 200}
              className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-1.5 rounded-sm transition-colors disabled:opacity-50 bg-amber-500 text-zinc-950 hover:bg-amber-400">
              <Stethoscope size={14} /> {busy ? "Treating…" : "Treat for 200 Gold"}
            </button>
          </div>
        </div>
      </Modal>



      <Modal open={cryOpen && !!sel} onClose={() => setCryOpen(false)} title={sel ? `Battle Cry — ${sel.name}` : "Battle Cry"}>
        <div data-testid="battle-cry-modal" className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          {!catalog || !sel ? <Loader /> : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-zinc-500">Shouted as the fight begins. <span className="text-zinc-400">{catalog.unlocked_count}/{catalog.total}</span> unlocked — earned across your whole ludus.</p>
                <button data-testid="clear-battle-cry-btn" onClick={() => chooseCry(null)} disabled={busy || !sel.battle_cry}
                  className="shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm transition-colors disabled:opacity-40">
                  Clear
                </button>
              </div>
              {catalog.categories.map((cat) => (
                <div key={cat.name} data-testid={`cry-cat-${cat.name}`}>
                  <div className="text-[10px] uppercase tracking-widest text-amber-500/80 mb-1.5 border-t border-amber-900/20 pt-2">{cat.name}</div>
                  <div className="space-y-1.5">
                    {cat.cries.map((c) => {
                      const selected = sel.battle_cry === c.id;
                      return (
                        <button key={c.id} data-testid={`cry-option-${c.id}`}
                          disabled={busy || !c.unlocked} onClick={() => c.unlocked && chooseCry(c.id)}
                          title={c.unlocked ? "Equip this cry" : c.how}
                          className={`w-full text-left px-3 py-2 border rounded-sm flex items-center justify-between gap-3 transition-colors ${
                            selected ? "border-amber-500 bg-amber-900/20 text-amber-200"
                            : !c.unlocked ? "border-zinc-800 bg-zinc-900/30 text-zinc-600 cursor-not-allowed"
                            : "border-zinc-800 bg-zinc-900/40 text-zinc-200 hover:border-amber-600"}`}>
                          <span className="flex items-center gap-2 text-sm min-w-0">
                            {!c.unlocked && <Lock size={12} className="text-zinc-600 shrink-0" />}
                            <span className="truncate">{c.text}</span>
                          </span>
                          {c.unlocked
                            ? (selected ? <Check size={14} className="text-amber-400 shrink-0" /> : null)
                            : <span className="text-[10px] uppercase tracking-wide text-zinc-600 shrink-0 text-right max-w-[55%]">{c.how}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </Modal>

      {/* Trait Selection Modal */}
      {traitModalGlad && (
        <TraitSelectionModal
          gladiator={traitModalGlad}
          onConfirm={handleAssignTraits}
          onClose={() => setTraitModalGlad(null)}
        />
      )}

      {/* Instant Scout Confirmation Modal */}
      {instantScoutGlad && (
        <Modal open={instantScoutOpen} onClose={() => setInstantScoutOpen(false)} title="Use Instant Scout">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-orange-950/20 border border-orange-800/40 rounded-sm">
              <img src="/instant-scout.png" alt="Instant Scout" className="w-12 h-12 object-contain" />
              <div>
                <h3 className="font-cinzel text-amber-300 text-sm">Instant Scout</h3>
                <p className="text-xs text-zinc-400">Fully reveal gladiator's potential (100%)</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-zinc-300 mb-2">
                This will consume 1 Instant Scout scroll to fully reveal <span className="text-amber-400 font-semibold">{instantScoutGlad.name}</span>'s potential.
              </p>
              {instantScoutGlad.potential_band?.percent > 0 && instantScoutGlad.potential_band?.percent < 100 && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded-sm">
                  <p className="text-xs text-amber-300">
                    ⚠ This gladiator is already <span className="font-semibold">{instantScoutGlad.potential_band.percent}% scouted</span>. Using this item will instantly reveal the remaining unknown potential.
                  </p>
                </div>
              )}
            </div>

            <div className="text-xs text-zinc-500">
              <p>• Current potential band: <span className="text-orange-400">{instantScoutGlad.potential_band?.label}</span></p>
              <p>• After use: Exact potential value revealed</p>
              <p>• Remaining scrolls: <span className="text-amber-400">{instantScoutCount - 1}</span></p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setInstantScoutOpen(false)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={useInstantScout}
                className={btn() + " flex-1 bg-orange-600 hover:bg-orange-500"}
                disabled={busy}
              >
                {busy ? "Using..." : "Use Instant Scout"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Experienced Medicus Modal */}
      {medicusGlad && (
        <Modal open={medicusOpen} onClose={() => setMedicusOpen(false)} title="⚕️ Use Experienced Medicus">
          <div className="space-y-4">
            {/* Item Display */}
            <div className="border border-amber-900/40 bg-gradient-to-br from-amber-950/20 to-orange-950/20 rounded-sm p-4">
              <div className="flex items-center gap-3 mb-3">
                <img src="/medicus.png" alt="Experienced Medicus" className="w-16 h-16 object-contain" />
                <div className="flex-1">
                  <div className="text-base font-cinzel text-amber-300 font-semibold">Experienced Medicus</div>
                  <div className="text-xs text-zinc-400 mt-1">Heal one scar with 100% success rate</div>
                  <div className="text-xs text-amber-500 mt-1">
                    Remaining: <span className="font-semibold">{medicusCount}</span>
                  </div>
                </div>
              </div>
              <div className="text-sm text-zinc-300 border-t border-amber-900/40 pt-3">
                Select which scar to heal on <span className="text-amber-300 font-cinzel font-semibold">{medicusGlad.name}</span>
              </div>
            </div>

            {/* Scar Selection */}
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-cinzel">Available Scars ({medicusGlad.scars?.length || 0})</div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {medicusGlad.scars && medicusGlad.scars.map((scar, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setMedicusConfirm({ gladiator: medicusGlad, scar, scarIndex: index });
                    }}
                    disabled={busy}
                    className="w-full text-left p-3 bg-zinc-900/80 hover:bg-orange-950/30 border border-zinc-800 hover:border-orange-600/60 rounded-sm transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-cinzel font-semibold text-zinc-200 group-hover:text-orange-300 transition-colors mb-2">
                          {scar.name || "Battle Scar"}
                        </div>
                        
                        {/* Scar Properties */}
                        <div className="space-y-1">
                          {scar.pct && scar.neg && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-rose-400 font-semibold">
                                −{scar.pct}% {scar.neg}
                              </span>
                            </div>
                          )}
                          {scar.pct && scar.pos && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-emerald-400 font-semibold">
                                +{scar.pct}% {scar.pos}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-amber-400 font-semibold">
                              −2% training speed
                            </span>
                          </div>
                        </div>
                        
                        {scar.acquired && (
                          <div className="text-[10px] text-zinc-600 mt-2">
                            Acquired: {new Date(scar.acquired).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-orange-400 group-hover:text-orange-300 uppercase tracking-wide font-cinzel">
                          Select
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2 font-cinzel">Treatment Details</div>
              <div className="space-y-1 text-xs text-zinc-400">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  <span>Success rate: <span className="text-emerald-400 font-semibold">100%</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  <span>After use: <span className="text-amber-400 font-semibold">{medicusCount - 1}</span> Medicus remaining</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  <span className="text-rose-400">This action cannot be undone</span>
                </div>
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => setMedicusOpen(false)}
              className={btn() + " w-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Medicus Confirmation Modal */}
      {medicusConfirm && (
        <Modal open={!!medicusConfirm} onClose={() => setMedicusConfirm(null)} title="⚕️ Confirm Healing">
          <div className="space-y-4">
            {/* Confirmation Message */}
            <div className="text-center py-4">
              <p className="text-sm text-zinc-300 mb-4">
                Use Experienced Medicus to heal this scar on <span className="text-amber-300 font-cinzel font-semibold">{medicusConfirm.gladiator.name}</span>?
              </p>
              
              {/* Scar Details */}
              <div className="bg-zinc-900/80 border border-orange-600/40 rounded-sm p-4 text-left">
                <div className="text-base font-cinzel font-semibold text-orange-300 mb-3">
                  {medicusConfirm.scar.name}
                </div>
                <div className="space-y-1.5">
                  {medicusConfirm.scar.pct && medicusConfirm.scar.neg && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-rose-400 font-semibold">
                        −{medicusConfirm.scar.pct}% {medicusConfirm.scar.neg}
                      </span>
                      <span className="text-zinc-600">→</span>
                      <span className="text-emerald-400">Removed</span>
                    </div>
                  )}
                  {medicusConfirm.scar.pct && medicusConfirm.scar.pos && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-emerald-400 font-semibold">
                        +{medicusConfirm.scar.pct}% {medicusConfirm.scar.pos}
                      </span>
                      <span className="text-zinc-600">→</span>
                      <span className="text-zinc-400">Removed</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-amber-400 font-semibold">
                      −2% training speed
                    </span>
                    <span className="text-zinc-600">→</span>
                    <span className="text-emerald-400">Restored</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-rose-950/20 border border-rose-900/40 rounded-sm p-3">
              <p className="text-xs text-rose-300 text-center">
                This action cannot be undone. The scar will be permanently removed.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setMedicusConfirm(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={applyMedicus}
                className={btn() + " flex-1 bg-orange-600 hover:bg-orange-500"}
                disabled={busy}
              >
                {busy ? "Healing..." : "Confirm Healing"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Recovery Ether Modal */}
      {recoveryEtherGlad && (
        <Modal open={recoveryEtherOpen} onClose={() => setRecoveryEtherOpen(false)} title="⚡ Use Recovery Ether">
          <div className="space-y-4">
            {/* Item Display */}
            <div className="flex items-center gap-3 p-4 bg-orange-950/20 border border-orange-800/40 rounded-sm">
              <img src="/recovery-ether.png" alt="Recovery Ether" className="w-12 h-12 object-contain" />
              <div>
                <h3 className="font-cinzel text-amber-300 text-sm">Recovery Ether</h3>
                <p className="text-xs text-zinc-400">Reduce battle rest cooldown by 2 hours</p>
              </div>
            </div>

            {/* Gladiator Info */}
            <div>
              <p className="text-sm text-zinc-300 mb-2">
                Use Recovery Ether on <span className="text-amber-400 font-cinzel font-semibold">{recoveryEtherGlad.name}</span> to reduce their rest time?
              </p>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-400">
                  <span className="text-orange-400 font-semibold">Current rest time:</span> {recoveryEtherGlad.fight_cooldown_seconds ? `${Math.floor(recoveryEtherGlad.fight_cooldown_seconds / 3600)}h ${Math.floor((recoveryEtherGlad.fight_cooldown_seconds % 3600) / 60)}m` : 'Ready'}
                </p>
              </div>
            </div>

            {/* Details */}
            <div className="text-xs text-zinc-500 space-y-1">
              <p>• Rest time will be reduced by <span className="text-orange-400 font-semibold">2 hours</span></p>
              <p>• Remaining Recovery Ethers: <span className="text-amber-400 font-semibold">{recoveryEtherCount - 1}</span></p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setRecoveryEtherOpen(false)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={applyRecoveryEther}
                className={btn() + " flex-1 bg-orange-600 hover:bg-orange-500"}
                disabled={busy}
              >
                {busy ? "Using..." : "Use Recovery Ether"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-manrope">{value}</div>
    </div>
  );
}
